<template>
  <div
    class="s-waveMask"
    :style="containerStyle"
    aria-hidden="true">
    <div class="s-waveMask__bg" />
    <div
      class="s-waveMask__played"
      :style="playedStyle" />
  </div>
</template>

<script setup>
/**
 * Explanation for how this component works:
 * s-waveMask is a wrapper that has a CSS mask applied to it.
 * The mask is mostly transparent with the audio-volume-bars painted in white.
 * Only the white parts "show through" the mask.
 * The background is then just gray (s-waveMask__bg). This makes the bars gray when progress is 0.
 * As progress grows > 0, we set the width of s-waveMask__played to be the same percentage as the progress
 * Because __played is on top of __bg, it colors the bars up until that point in progress
 * This:
 *  - is very performant (no redrawing, just changing the transform of __played. Also, only 3 dom elements)
 *  - automatically gives us sub-bar progress indication compared to - for example - just having 1 dom element per bar and coloring it if it has been played.
 */
import {
  computed, ref, watch, onMounted,
} from 'vue'

import { audioWaveMaskCache } from '@/modules/global/logic/audioWaveMaskCache'

const HEIGHT = 28

const props = defineProps({
  volumes: { type: Array, required: true },
  width: { type: Number, required: true },
  // Current progress [0..1]
  progress: { type: Number, default: 0 },
})

const emit = defineEmits(['ready'])

// blob URL for CSS mask
const maskUrl = ref(null)

const dpr = computed(() => {
  if (!window.devicePixelRatio) {
    return 1
  }
  return window.devicePixelRatio
})
const containerStyle = computed(() => {
  return {
    width: `${props.width}px`,
    height: `${HEIGHT}px`,
    '--mask-url': maskUrl.value ? `url("${maskUrl.value}")` : 'none',
    opacity: maskUrl.value ? 1 : 0,
  }
})

const playedStyle = computed(() => {
  return {
    transform: `scaleX(${props.progress})`,
  }
})

// Token to ensure last async call wins
let lastRequestId = 0

async function updateMask() {
  const requestId = ++lastRequestId
  maskUrl.value = null

  if (!props.width || props.width <= 0 || !props.volumes || props.volumes.length === 0) {
    return
  }

  try {
    const url = await audioWaveMaskCache.getAndCacheMask({
      volumes: props.volumes,
      width: props.width,
      dpr: dpr.value,
    })
    // Only apply if this is the latest request
    if (requestId === lastRequestId) {
      maskUrl.value = url
      if (url) {
        emit('ready')
      }
    }
  } catch (_) {
    /* swallow */
  }
}

// Regenerate mask if any geometry-affecting input changes
watch(() => props.volumes, updateMask)
watch(() => props.width, updateMask)

onMounted(() => {
  updateMask()
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/config';

.s-waveMask {
  position: relative;
  overflow: hidden;
  contain: paint;
  mask-image: var(--mask-url);
  mask-repeat: no-repeat;
  mask-size: 100% 100%;
}

.s-waveMask__bg,
.s-waveMask__played {
  position: absolute;
  background: $color-dark_light_emphasis;
  width: 100%;
  height: 100%;
}

/* Played overlay uses transform-only reveal */
.s-waveMask__played {
  background: $color-primary;
  transform-origin: left center;
  transform: scaleX(0);
  will-change: transform;
}
</style>
